
import type { ResizeObserver } from 'bits/global'
import { observeSizeChange } from 'bits/resize_observer'
import { Component, defineAsyncComponent, Prop, Vue, Watch } from 'bits/vue'
import OzTextButton, { OzTextButtonColorScheme } from 'library/v4/components/OzTextButton.vue'
import tinykeys from 'tinykeys'

const OzIcon = defineAsyncComponent(() => import('library/v4/components/OzIcon.vue'))

interface OzTabsButtonProps {
  /**
   * A string to identify the tab.
   */
  key: string
  /**
   * The text to display on the tab.
   * If `iconName` is provided, this will be used as the `aria-label` instead.
   */
  text: string
  /**
   * The name of the icon to display on the tab.
   * We don't display `text` if this is provided.
   */
  iconName?: string
  /**
   * The name of the icon to display on the tab when it's active.
   */
  activeIconName?: string
  /**
   * The size of the icon to display on the tab.
   */
  iconSize?: 16 | 24
  /**
   * `aria-controls` attribute.
   */
  ariaControls?: string
  /**
   * `href` attribute.
   * This also makes the tab a link.
   */
  href?: string
  /**
   * should the tab be disabled or not
   */
  disabled?: boolean
  /**
   * Id for querying tab in tests
   */
  testId?: string
}

enum OzTabsAccessibilityRole {
  Tabs = 'Tabs',
  Navigation = 'Navigation',
}

enum OzTabsPlacementMode {
  Hug = 'Hug',
  Stretch = 'Stretch',
}

enum OzTabsSizePreset {
  ExtraSmall = 'ExtraSmall',
  Small = 'Small',
}

/**
 * Orizuru Tabs.
 */

@Component({
  components: {
    OzIcon,
    OzTextButton,
  },
})
export default class OzTabs extends Vue {
  PlacementMode = OzTabsPlacementMode
  SizePreset = OzTabsSizePreset
  OzTextButtonColorScheme = OzTextButtonColorScheme

  $refs!: {
    list: HTMLDivElement
    indicator: HTMLDivElement
  }

  /**
   * Accommodates the elements and attributes according to the use.
   * If `Tabs`, the component will be structured according to the ARIA tab list specification.
   * If `Navigation`, the component will be structured according to the ARIA navigation specification.
   */
  @Prop({ default: OzTabsAccessibilityRole.Tabs })
  accessibilityRole!: OzTabsAccessibilityRole

  /**
   * Label corresponding to the tab list.
   */
  @Prop({ required: true }) ariaLabel!: string

  /**
   * Dark mode.
   */
  @Prop({ default: 'auto' }) darkMode!: boolean | 'auto'

  /**
   * Key used to detect the current tab.
   */
  @Prop({ required: true }) currentTabKey!: string

  /**
   * Tabs placement mode.
   *
   * **Hug**
   *
   * - Gap between tabs is dynamic, and each tab
   * occupies as little space as possible.
   *
   *
   * **Stretch**
   *
   * - No gap between tabs, and each tab
   * has the same width.
   */
  @Prop({ default: OzTabsPlacementMode.Hug }) placementMode!: OzTabsPlacementMode

  /**
   * Tabs size preset.
   *
   * **Small**
   *
   * - Font size: body-small
   * - Indicator height: 1.5px
   *
   *
   * **ExtraSmall**
   *
   * - Font size: body-extra-small
   * - Indicator height: 1px
   */
  @Prop({ default: OzTabsSizePreset.Small }) sizePreset!: OzTabsSizePreset

  /**
   * Tabs to display.
   */
  @Prop({ required: true }) tabs!: OzTabsButtonProps[]

  /**
   * Classes applied to the tabs container.
   */
  @Prop({ required: false }) tabsContainerClasses?: string

  /**
   * Override indicator width.
   */
  @Prop({ required: false }) indicatorWidth?: number

  resizeObserver: ResizeObserver | null = null
  removeKeyboardShortcutsListener: () => void = () => {}

  get highlightedElement(): HTMLElement | null {
    const ref = this.$refs[this.currentTabKey]
    return ref ? ref[0] : null
  }

  get wrapperElement(): string {
    return this.isUsingTabsAccessibilityRole ? 'div' : 'nav'
  }

  get wrapperAttributes(): Record<string, any> {
    return this.isUsingTabsAccessibilityRole ? {} : { 'aria-label': this.ariaLabel }
  }

  get listAttributes(): Record<string, any> {
    return this.isUsingTabsAccessibilityRole ? { 'aria-label': this.ariaLabel } : {}
  }

  get listRole(): string {
    return this.isUsingTabsAccessibilityRole ? 'tablist' : 'list'
  }

  get listItemRole(): string {
    return this.isUsingTabsAccessibilityRole ? 'presentation' : 'listitem'
  }

  get isUsingTabsAccessibilityRole(): boolean {
    return this.accessibilityRole === OzTabsAccessibilityRole.Tabs
  }

  computeTabAttributes(tab: OzTabsButtonProps): Record<string, any> {
    return this.isUsingTabsAccessibilityRole
      ? {
          id: tab.key,
          role: 'tab',
          'aria-controls': tab.ariaControls,
          'aria-label': tab.iconName ? tab.text : undefined,
          'aria-selected': this.currentTabKey === tab.key ? 'true' : 'false',
          tabindex: this.currentTabKey === tab.key ? 0 : -1,
        }
      : {
          role: tab.href ? 'link' : 'button',
        }
  }

  /**
   * Update the indicator's position.
   */
  @Watch('currentTabKey')
  @Watch('placementMode')
  @Watch('sizePreset')
  @Watch('tabsContainerClasses')
  @Watch('indicatorWidth')
  repositionIndicator(): void {
    this.$nextTick(() => {
      if (!this.highlightedElement) return

      const indicatorElement = this.$refs.indicator
      if (this.indicatorWidth) {
        indicatorElement.style.width = this.indicatorWidth + 'px'
        indicatorElement.style.left =
          this.highlightedElement.offsetLeft + (this.highlightedElement.clientWidth - this.indicatorWidth) / 2 + 'px'
      } else {
        indicatorElement.style.width = this.highlightedElement.offsetWidth + 'px'
        indicatorElement.style.left = this.highlightedElement.offsetLeft + 'px'
      }
    })
  }

  /**
   * Focus the first tab.
   */
  focusFirstTab(): void {
    if (!this.tabs.length) return
    this.$refs[this.tabs[0].key][0]?.firstChild?.focus()
  }

  /**
   * Focus the last tab.
   */
  focusLastTab(): void {
    if (!this.tabs.length) return
    this.$refs[this.tabs[this.tabs.length - 1].key][0]?.firstChild?.focus()
  }

  /**
   * When pressing "Home", focus the first tab.
   */
  handleHomeKey(event: KeyboardEvent): void {
    event.preventDefault()
    this.focusFirstTab()
  }

  /**
   * When pressing "End", focus the last tab.
   */
  handleEndKey(event: KeyboardEvent): void {
    event.preventDefault()
    this.focusLastTab()
  }

  /**
   * When pressing the left arrow, focus the previous tab.
   * If on first tab, focus the last tab.
   */
  handleArrowLeft(event: KeyboardEvent): void {
    const target = event.target as HTMLElement
    const wrapper = target.parentElement
    if (wrapper?.previousElementSibling) {
      const previousSibling = wrapper.previousElementSibling.firstChild as HTMLElement
      previousSibling?.focus()
    } else {
      this.focusLastTab()
    }
  }

  /**
   * When pressing the right arrow, focus the next tab.
   * If on last tab, focus the first tab.
   */
  handleArrowRight(event: KeyboardEvent): void {
    const target = event.target as HTMLElement
    const wrapper = target.parentElement
    if (wrapper?.nextElementSibling) {
      const nextSibling = wrapper.nextElementSibling.firstChild as HTMLElement
      nextSibling?.focus()
    } else {
      this.focusFirstTab()
    }
  }

  mounted(): void {
    this.repositionIndicator()
    this.resizeObserver = observeSizeChange(this.$el as HTMLDivElement, this.repositionIndicator)
    this.removeKeyboardShortcutsListener = tinykeys(this.$refs.list as HTMLElement, {
      Home: this.handleHomeKey,
      End: this.handleEndKey,
      ArrowLeft: this.handleArrowLeft,
      ArrowRight: this.handleArrowRight,
    })
  }

  beforeDestroy(): void {
    this.resizeObserver?.unobserve(this.$el)
    this.removeKeyboardShortcutsListener()
  }
}

export { OzTabsButtonProps, OzTabsAccessibilityRole, OzTabsPlacementMode, OzTabsSizePreset }
